/**
 * ClassificationSystem - Evidence-based specimen classification
 * From PRD Section 2.3.3
 */

import Phaser from 'phaser';
import type { EvidenceCard, ClassificationResult, ScanResult } from '@/types';
import { Classification } from '@/types';
import { generateEvidenceFromScan, CLASSIFICATION_FEEDBACK } from '@/data/evidence';
import type { Specimen } from './ScannerSystem';

export interface ClassificationSystemConfig {
  /** Minimum evidence required to classify (default: 3) */
  minEvidence?: number;
  /** Minimum evidence for expert mode (default: 5) */
  expertMinEvidence?: number;
  /** Expert mode enabled */
  expertMode?: boolean;
}

export interface ClassificationAttempt {
  specimenId: string;
  classification: Classification;
  evidence: EvidenceCard[];
  result: ClassificationResult;
  timestamp: number;
}

export interface ClassificationStateEvent {
  canClassify: boolean;
  evidenceCount: number;
  minRequired: number;
}

export interface ClassificationResultEvent {
  specimen: Specimen;
  classification: Classification;
  result: ClassificationResult;
  attempt: ClassificationAttempt;
}

/**
 * ClassificationSystem manages evidence collection and specimen classification
 */
export class ClassificationSystem {
  private scene: Phaser.Scene;
  private config: Required<ClassificationSystemConfig>;

  /** Collected evidence cards */
  private collectedEvidence: Map<string, EvidenceCard> = new Map();

  /** Current specimen being classified */
  private currentSpecimen: Specimen | null = null;

  /** Classification history */
  private attempts: ClassificationAttempt[] = [];

  constructor(scene: Phaser.Scene, config: ClassificationSystemConfig = {}) {
    this.scene = scene;
    this.config = {
      minEvidence: config.minEvidence ?? 3,
      expertMinEvidence: config.expertMinEvidence ?? 5,
      expertMode: config.expertMode ?? false,
    };

    this.setupEventListeners();
  }

  /**
   * Set up event listeners for evidence collection
   */
  private setupEventListeners(): void {
    // Listen for evidence collected from scanner
    this.scene.events.on('evidence:collected', this.handleEvidenceCollected, this);
    this.scene.events.on('scanner:complete', this.handleScanComplete, this);
  }

  /**
   * Handle evidence collected from scanner
   */
  private handleEvidenceCollected(evidence: EvidenceCard): void {
    this.collectEvidence(evidence);
  }

  /**
   * Handle scan complete - generate additional evidence from result
   */
  private handleScanComplete(event: { specimen: Specimen; result: ScanResult }): void {
    // Generate evidence from scan result using templates
    const generatedEvidence = generateEvidenceFromScan(event.result);

    for (const evidence of generatedEvidence) {
      // Add specimen ID to make evidence unique
      const uniqueEvidence: EvidenceCard = {
        ...evidence,
        id: `${event.specimen.id}_${evidence.id}`,
      };
      this.collectEvidence(uniqueEvidence);
    }

    // Set current specimen
    this.currentSpecimen = event.specimen;
  }

  /**
   * Collect an evidence card
   */
  public collectEvidence(evidence: EvidenceCard): void {
    // Avoid duplicates
    if (this.collectedEvidence.has(evidence.id)) return;

    this.collectedEvidence.set(evidence.id, evidence);

    // Emit state change event
    this.emitStateChange();

    // Emit evidence added event
    this.scene.events.emit('classification:evidence:added', evidence);
  }

  /**
   * Get all collected evidence
   */
  public getCollectedEvidence(): EvidenceCard[] {
    return Array.from(this.collectedEvidence.values());
  }

  /**
   * Get evidence that supports a classification
   */
  public getEvidenceFor(classification: Classification): EvidenceCard[] {
    return this.getCollectedEvidence().filter(e =>
      e.supportsClassification.includes(classification)
    );
  }

  /**
   * Get evidence that contradicts a classification
   */
  public getEvidenceAgainst(classification: Classification): EvidenceCard[] {
    return this.getCollectedEvidence().filter(e =>
      e.contradictsClassification.includes(classification)
    );
  }

  /**
   * Get minimum evidence required
   */
  public getMinEvidenceRequired(): number {
    return this.config.expertMode
      ? this.config.expertMinEvidence
      : this.config.minEvidence;
  }

  /**
   * Check if player can classify
   */
  public canClassify(): boolean {
    return this.collectedEvidence.size >= this.getMinEvidenceRequired();
  }

  /**
   * Check if a specific classification is supported by evidence
   */
  public isClassificationSupported(classification: Classification): boolean {
    const supporting = this.getEvidenceFor(classification);
    const contradicting = this.getEvidenceAgainst(classification);

    // Supported if more supporting than contradicting
    return supporting.length > contradicting.length;
  }

  /**
   * Get the strength of evidence for a classification (0-1)
   */
  public getClassificationStrength(classification: Classification): number {
    const supporting = this.getEvidenceFor(classification).length;
    const total = this.collectedEvidence.size;

    if (total === 0) return 0;
    return supporting / total;
  }

  /**
   * Classify a specimen
   */
  public classify(
    specimen: Specimen,
    classification: Classification
  ): ClassificationResult {
    // Check if can classify
    if (!this.canClassify()) {
      return {
        correct: false,
        feedback: CLASSIFICATION_FEEDBACK.incorrect.needMoreEvidence,
      };
    }

    // Validate classification against specimen
    const result = this.validateClassification(specimen, classification);

    // Record attempt
    const attempt: ClassificationAttempt = {
      specimenId: specimen.id,
      classification,
      evidence: this.getCollectedEvidence(),
      result,
      timestamp: Date.now(),
    };
    this.attempts.push(attempt);

    // Emit result event
    const resultEvent: ClassificationResultEvent = {
      specimen,
      classification,
      result,
      attempt,
    };

    if (result.correct) {
      this.scene.events.emit('classification:correct', resultEvent);
    } else {
      this.scene.events.emit('classification:incorrect', resultEvent);
    }

    this.scene.events.emit('classification:complete', resultEvent);

    return result;
  }

  /**
   * Validate a classification against specimen properties
   */
  private validateClassification(
    specimen: Specimen,
    classification: Classification
  ): ClassificationResult {
    switch (classification) {
      case Classification.LIVING:
        if (specimen.isLiving) {
          return {
            correct: true,
            feedback: CLASSIFICATION_FEEDBACK.correct.living,
          };
        }
        return {
          correct: false,
          feedback: CLASSIFICATION_FEEDBACK.incorrect.nonLivingAsLiving,
          expectedClassification: Classification.NON_LIVING,
        };

      case Classification.NON_LIVING:
        if (!specimen.isLiving) {
          return {
            correct: true,
            feedback: CLASSIFICATION_FEEDBACK.correct.nonLiving,
          };
        }
        return {
          correct: false,
          feedback: CLASSIFICATION_FEEDBACK.incorrect.livingAsNonLiving,
          expectedClassification: Classification.LIVING,
        };

      case Classification.UNICELLULAR:
        if (this.isUnicellular(specimen)) {
          return {
            correct: true,
            feedback: CLASSIFICATION_FEEDBACK.correct.unicellular,
          };
        }
        return {
          correct: false,
          feedback: CLASSIFICATION_FEEDBACK.incorrect.multicellularAsUnicellular,
          expectedClassification: Classification.MULTICELLULAR,
        };

      case Classification.MULTICELLULAR:
        if (this.isMulticellular(specimen)) {
          return {
            correct: true,
            feedback: CLASSIFICATION_FEEDBACK.correct.multicellular,
          };
        }
        return {
          correct: false,
          feedback: CLASSIFICATION_FEEDBACK.incorrect.unicellularAsMulticellular,
          expectedClassification: Classification.UNICELLULAR,
        };

      default:
        return {
          correct: false,
          feedback: 'Unknown classification type.',
        };
    }
  }

  /**
   * Check if specimen is unicellular
   */
  private isUnicellular(specimen: Specimen): boolean {
    return specimen.cellCount === 'single' || specimen.cellCount === 1;
  }

  /**
   * Check if specimen is multicellular
   */
  private isMulticellular(specimen: Specimen): boolean {
    return (
      specimen.cellCount === 'multiple' ||
      (typeof specimen.cellCount === 'number' && specimen.cellCount > 1)
    );
  }

  /**
   * Emit state change event
   */
  private emitStateChange(): void {
    const event: ClassificationStateEvent = {
      canClassify: this.canClassify(),
      evidenceCount: this.collectedEvidence.size,
      minRequired: this.getMinEvidenceRequired(),
    };
    this.scene.events.emit('classification:state', event);
  }

  /**
   * Set current specimen for classification
   */
  public setCurrentSpecimen(specimen: Specimen): void {
    this.currentSpecimen = specimen;
  }

  /**
   * Get current specimen
   */
  public getCurrentSpecimen(): Specimen | null {
    return this.currentSpecimen;
  }

  /**
   * Set expert mode
   */
  public setExpertMode(enabled: boolean): void {
    this.config.expertMode = enabled;
    this.emitStateChange();
  }

  /**
   * Check if in expert mode
   */
  public isExpertMode(): boolean {
    return this.config.expertMode;
  }

  /**
   * Get classification attempts
   */
  public getAttempts(): ClassificationAttempt[] {
    return [...this.attempts];
  }

  /**
   * Get accuracy (correct / total attempts)
   */
  public getAccuracy(): number {
    if (this.attempts.length === 0) return 0;

    const correct = this.attempts.filter(a => a.result.correct).length;
    return correct / this.attempts.length;
  }

  /**
   * Reset collected evidence
   */
  public clearEvidence(): void {
    this.collectedEvidence.clear();
    this.emitStateChange();
    this.scene.events.emit('classification:evidence:cleared');
  }

  /**
   * Reset the system completely
   */
  public reset(): void {
    this.clearEvidence();
    this.currentSpecimen = null;
    this.attempts = [];
    this.scene.events.emit('classification:reset');
  }

  /**
   * Get a hint for the current specimen
   */
  public getHint(): string {
    if (!this.currentSpecimen) {
      return 'Scan a specimen to collect evidence.';
    }

    // Based on evidence collected, give appropriate hint
    const evidenceCount = this.collectedEvidence.size;

    if (evidenceCount < this.getMinEvidenceRequired()) {
      return `Collect ${this.getMinEvidenceRequired() - evidenceCount} more evidence to classify.`;
    }

    // Check which classifications have the most support
    const livingStrength = this.getClassificationStrength(Classification.LIVING);
    const nonLivingStrength = this.getClassificationStrength(Classification.NON_LIVING);

    if (livingStrength > nonLivingStrength) {
      return CLASSIFICATION_FEEDBACK.hints.living;
    } else if (nonLivingStrength > livingStrength) {
      return CLASSIFICATION_FEEDBACK.hints.nonLiving;
    }

    return 'Review your evidence carefully before classifying.';
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off('evidence:collected', this.handleEvidenceCollected, this);
    this.scene.events.off('scanner:complete', this.handleScanComplete, this);

    this.collectedEvidence.clear();
    this.attempts = [];
    this.currentSpecimen = null;
  }
}
